//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';

// Shared shape for a Task's display icon, as sent alongside task_id/task_name
// when a Task is selected (see AIModule's lastTaskIconRef / TasksTab). Kept
// here so every place that renders a task icon (the one-time task banner in
// ChatThread and the persistent badge in ChatHeader) agrees on the same
// fields instead of re-declaring the shape.
export interface TaskIcon {
  name?: string;
  emoji?: string;
  color?: string;
  data?: string;
}

export interface RenderTaskIconOptions {
  /** Pixel size of the rendered icon (image or emoji box). Defaults to 28. */
  size?: number;
  /** data-testid to attach to the emoji fallback span, if a test needs to target it. */
  testId?: string;
}

/**
 * Renders a task's icon: a base64/URL image if `icon.data` is present,
 * otherwise the icon's emoji (or a default sparkle). `icon.data` may be a
 * bare base64 payload (mime inferred from `icon.name`'s extension) or an
 * already-prefixed data:/http(s):/relative URL.
 */
export function renderTaskIcon(
  icon: TaskIcon | undefined,
  name: string,
  { size = 28, testId }: RenderTaskIconOptions = {},
): React.ReactNode {
  const raw = icon?.data?.trim();
  if (raw) {
    const isUrl = /^(data:|https?:\/\/|\/|\.\/|\.\.\/)/i.test(raw);
    let src = raw;
    if (!isUrl) {
      const icName = (icon?.name || '').toLowerCase();
      let mime = 'image/png';
      if (icName.endsWith('.svg')) mime = 'image/svg+xml';
      else if (icName.endsWith('.jpg') || icName.endsWith('.jpeg'))
        mime = 'image/jpeg';
      else if (icName.endsWith('.gif')) mime = 'image/gif';
      src = `data:${mime};base64,${raw}`;
    }
    return (
      <img
        src={src}
        alt={name}
        style={{ width: size, height: size, objectFit: 'contain' }}
      />
    );
  }
  return (
    <span {...(testId ? { 'data-testid': testId } : {})}>
      {icon?.emoji || '✨'}
    </span>
  );
}
